import { Component, HostBinding, Inject, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { IModalsService, IMODALS_SERVICE, P500UIBaseModal, TooltipDirective } from '@platform500services/p500-ui-kit';
import { ClipboardServiceInterface } from '../../services/clipboard/clipboard-service.interface';
import { Router } from '@angular/router';

@Component({
  templateUrl: './info.modal.html',
})
export class InfoModal extends P500UIBaseModal implements OnInit, OnDestroy {
  @HostBinding('class') get className() {
    return `p500-ui-modal p500-ui-modal--primary p500-ui-modal--small c-modal c-modal--info ${this.data?.class || ''}`;
  }

  @Input() data: InfoModalData;
  @ViewChild(TooltipDirective) tooltipDirective: TooltipDirective;
  @ViewChild('footerRef', { read: ViewContainerRef }) footerRef: ViewContainerRef;
  public prettyBodyText = '';
  private timeout: any;
  public FOOTER_TYPES = FooterType;
  @Input() onClose: () => void = () => {};
  @Input() onFooterClose: () => void = () => {};

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    @Inject('ClipboardServiceInterface') private clipboardService: ClipboardServiceInterface,
    public router: Router
  ) {
    super(modalsService, router);
  }

  ngOnInit(): void {
    try {
      const parsedBodyText = JSON.parse(this.data.body);
      this.prettyBodyText = JSON.stringify(parsedBodyText, null, 2).trim();
    } catch {
      this.prettyBodyText = this.data.body;
    }
  }

  closeModal(): void {
    this.onClose();
    this.modalsService.close(this.uniqueId);
  }

  footerCancel = () => this.modalsService.close(this.uniqueId);
  footerClose = () => {
    this.modalsService.close(this.uniqueId);
    this.onFooterClose();
  };

  public copy(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.clipboardService.copyFromContent(this.prettyBodyText);
    this.tooltipDirective.updateText($localize`:@@common.copied_to_clipboard:Copied to clipboard`);
    this.timeout = setTimeout(() => {
      this.tooltipDirective.updateText($localize`:@@common.copy_to_clipboard:Copy to clipboard`);
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
}

enum FooterType {
  'ATTENTION' = 'ATTENTION',
  'CLOSE_CANCEL' = 'CLOSE_CANCEL',
}

export const INFO_MODAL_DATA: { [key: string]: InfoModalData } = {
  PRODUCT_CONFIGURATION: {
    title: $localize`:@@info_modal.attention_title:Attention!`,
    subTitle: '',
    body: $localize`:@@info_modal.product_configuration_body:Something is wrong with your product configuration. Please contact administrator for further instructions.`,
    footerType: FooterType.ATTENTION,
    hasCopy: true,
    class: '-danger',
  },
  OFFER_SOURCE: {
    title: $localize`:@@info_modal.warning_title:Warning!`,
    subTitle: '',
    body: $localize`:@@info_modal.offer_source_body:
    By selecting direct link you'll acknowledge that:
    </br>
    -The chance of a ban is higher due to a lack of intermediate redirects that would protect the link
    </br>
    -Link is harder to change since it's bound to funnel domain and the affiliate would also need to replace it manually
    </br>
    -Direct link could have higher C2L due to the lack of intermediary and potentially faster loading time.`,
    footerType: null,
    hasCopy: false,
    class: '-danger',
  },
  UNSAVED_DATA: {
    title: $localize`:@@info_modal.unsaved_changes_title:Unsaved Changes`,
    subTitle: 'There are unsaved changes',
    body: $localize`:@@info_modal.unsaved_changes_body:Changes that you’ve made won’t be saved. Are you sure you want to close the window?`,
    footerType: FooterType.CLOSE_CANCEL,
    hasCopy: false,
    class: '-warning',
  },
  PULL_CONVERION_LOGS: {
    title: $localize`:@@info_modal.pull_logs_title:Pull Logs`,
    subTitle: '',
    body: '',
    footerType: FooterType.CLOSE_CANCEL,
    hasCopy: false,
    class: 'p500-ui-modal--medium -info',
  },
  TEST_SETTINGS: {
    title: $localize`:@@info_modal.test_settings_logs_title:Test Settings Logs`,
    subTitle: '',
    body: '',
    footerType: FooterType.CLOSE_CANCEL,
    hasCopy: false,
    class: 'p500-ui-modal--medium -info',
  },
  OFFER_DESCRIPTION: {
    title: $localize`:@@info_modal.offer_description_title:Offer Description`,
    subTitle: '',
    body: '',
    footerType: null,
    hasCopy: false,
    class: 'p500-ui-modal--medium -info',
  },
};

export interface InfoModalData {
  title: string;
  subTitle: string;
  body: string;
  footerType: FooterType | null;
  hasCopy: boolean;
  class?: string;
}
